class ServerValidationError extends Error {
  constructor(errors) {
    super('Server validation error');
    this.errors = errors;
  }
}

const makeRequest = async (url, method, headers, body) => {
  // console.log('request:', JSON.stringify({ url, method, headers, body }));
  const response = await fetch(url, { method, headers, ...(body && { body: JSON.stringify(body) }) });
  // console.log('response:', JSON.stringify({ url, method, headers, body, response }));

  if (!response.ok) {
    const responseJson = await response.json();

    if (response.status === 400 && responseJson.validationFailed) {
      throw new ServerValidationError(responseJson.errors);
    } else {
      throw new Error('Unexpected server error');
    }
  }

  return response;
};

const contentTypeJson = { 'Content-Type': 'application/json' };
const extractJsonFromResponse = async (response) => response && (await response.json());

const loginUser = async (userData) =>
  await extractJsonFromResponse(await makeRequest('/api/user/login', 'POST', contentTypeJson, userData));

const getActivationCodeData = async (activationCode) =>
  await extractJsonFromResponse(await makeRequest(`/api/employer/activate/${activationCode}`, 'GET', contentTypeJson));

const getClientParameters = async () =>
  await extractJsonFromResponse(await makeRequest(`/api/client/params`, 'GET', contentTypeJson));

const createUserApi = (getToken, onUpdate) => {
  const authHeader = async () => ({ Authorization: `Bearer ${await getToken()}` });

  const makeGetJsonRequest = async (url) =>
    await extractJsonFromResponse(await makeRequest(url, 'GET', { ...(await authHeader()), ...contentTypeJson }));

  const makePostJsonRequest = async (url, body, triggerUpdate = true) => {
    const result = await extractJsonFromResponse(
      await makeRequest(url, 'POST', { ...(await authHeader()), ...contentTypeJson }, body),
    );
    if (triggerUpdate) {
      await onUpdate();
    }
    return result;
  };

  const downloadFile = async (url) => {
    const response = await makeRequest(url, 'GET', await authHeader());
    if (!response.ok) {
      return false;
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(await response.blob());
    link.target = '_blank';
    link.click();
  };

  const makeGetter = (url) => () => makeGetJsonRequest(url);
  const makePoster = (url) => (input) => makePostJsonRequest(url, input);

  const makeUserGetter = (path) => makeGetter(`/api/user/${path}`);
  const makeUserPoster = (path) => makePoster(`/api/user/${path}`);

  const createAccount = makeUserPoster('signup');
  const getUser = makeUserGetter(`get`);
  const getUserEmployments = makeUserGetter(`getEmployments`);
  const acceptEmployment = async (employmentId) =>
    await makePostJsonRequest(`/api/user/acceptEmployment/${employmentId}`);
  const requestPayrollElection = async (employmentId, values) =>
    await makePostJsonRequest(`/api/user/updatePayrollElection/${employmentId}`, values);
  const updateUserName = makeUserPoster(`updateName`);
  const updateUserLanguagePreference = makeUserPoster(`updateLanguagePreference`);
  const updateUserBirthday = makeUserPoster(`updateBirthday`);
  const updateUserAddress = makeUserPoster(`updateAddress`);
  const updateUserTaxId = makeUserPoster(`updateTaxId`);
  const activateUserEmployment = makeUserPoster(`activateEmployment`);
  const updateUserEmploymentStatus = makeUserPoster(`updateEmploymentStatus`);
  const updateUserFundingSource = makeUserPoster(`updateFundingSource`);
  const updateUserResidencyStatus = makeUserPoster(`updateResidencyStatus`);
  const updateUserDisclosures = makeUserPoster(`updateDisclosures`);
  const updateUserMaritalStatus = makeUserPoster(`updateMaritalStatus`);
  const updateUserBeneficiaries = makeUserPoster(`updateBeneficiaries`);
  const updateUserTrustedContact = makeUserPoster(`updateTrustedContact`);
  const updateUserPhoneNumber = makeUserPoster(`updatePhoneNumber`);
  const getRequiredAgreements = makeUserGetter(`getRequiredAgreements`);
  const acceptAgreements = makeUserPoster(`acceptAgreements`);
  const updateUserAccountLossRiskQuestion = makeUserPoster(`updateAccountLossRiskQuestion`);
  const updateUserInvestmentObjectiveRiskQuestion = makeUserPoster(`updateInvestmentObjectiveRiskQuestion`);
  const updateUserIncomeLevel = makeUserPoster(`updateIncomeLevel`);
  const updateUserNetWorth = makeUserPoster(`updateNetWorth`);
  const updateUserIsThirdPartyPayrollAdmin = makeUserPoster(`updateIsThirdPartyPayrollAdmin`);
  const updateUserAdminEmployer = makeUserPoster(`updateAdminEmployer`);
  const completeUserAdminOnboarding = makeUserPoster(`completeAdminOnboarding`);
  const getUserTransferOptions = makeUserGetter(`transferOptions`);

  const makeAccountGetter = (path) => (investmentAccountId) =>
    makeGetJsonRequest(`/api/investmentAccount/${investmentAccountId}/${path}`);
  const makeAccountPoster =
    (path) =>
    (investmentAccountId, input = {}) =>
      makePostJsonRequest(`/api/investmentAccount/${investmentAccountId}/${path}`, input);

  const updateInvestmentAccountType = makeAccountPoster(`updateAccountType`);
  const submitInvestmentAccount = makeAccountPoster(`submit`);

  const getUserTransferHistory = makeAccountGetter(`getTransferHistory`);
  const getUserTransferRequests = makeAccountGetter(`getTransferRequests`);
  const getUserRecurringDeposits = makeAccountGetter(`getRecurringDeposits`);

  const createRecurringDeposit = makeAccountPoster(`createRecurringDeposit`);
  const updateRecurringDeposit = makeAccountPoster(`updateRecurringDeposit`);
  const cancelRecurringDeposit = makeAccountPoster(`cancelRecurringDeposit`);

  const createIncomingTransferRequest = makeAccountPoster(`createIncomingTransferRequest`);
  const createOutgoingTransferRequest = makeAccountPoster(`createOutgoingTransferRequest`);
  const cancelTransferRequest = makeAccountPoster(`cancelTransferRequest`);

  const getPlaidLinkedAccounts = makeGetter('/api/link/get');
  const getPlaidLinkedAccountsAsList = async () =>
    (await getPlaidLinkedAccounts()).map(({ accounts }) => accounts).flat();

  const requestPlaidLinkToken = async (values) =>
    (await makePostJsonRequest(`/api/link/initiate`, values, false)).plaidLinkToken;
  const connectPlaidPublicToken = async (plaidPublicToken) =>
    await makePostJsonRequest(`/api/link/create`, { plaidPublicToken });

  const getInvestmentAccounts = makeGetter(`/api/investmentAccount/get`);

  const getInvestmentAccountStatements = makeAccountGetter(`statement/list`);

  const downloadInvestmentAccountStatement = async (investmentAccountId, documentId) =>
    await downloadFile(`/api/investmentAccount/${investmentAccountId}/statement/download/${documentId}`);

  const getAdministratedEmployers = makeGetter(`/api/employer/get`);
  const createEmployer = async (input) => await makePostJsonRequest(`/api/employer/create`, input);
  const updateEmployer = async (employerId, input) =>
    await makePostJsonRequest(`/api/employer/${employerId}/update`, input);

  const getEmployeesForEmployer = async (employerId) => await makeGetJsonRequest(`/api/employer/${employerId}/get`);
  const addEmployeeForEmployer = async (employerId, input) =>
    await makePostJsonRequest(`/api/employer/${employerId}/add`, input);

  const updateEmployeeForEmployer = async (employerId, employmentId, input) =>
    await makePostJsonRequest(`/api/employer/${employerId}/updateEmployment/${employmentId}`, input);

  const resendCodeForEmployee = async (employerId, employmentId) =>
    await makePostJsonRequest(`/api/employer/${employerId}/resetVerification/${employmentId}`, {});

  const confirmNameForEmployee = async (employerId, employmentId) =>
    await makePostJsonRequest(`/api/employer/${employerId}/confirmName/${employmentId}`, {});
  const confirmPayrollElectionEmployee = async (employerId, employmentId, values) =>
    await makePostJsonRequest(`/api/employer/${employerId}/confirmPayrollElection/${employmentId}`, values);

  return {
    createAccount,
    getUser,
    getUserEmployments,
    acceptEmployment,
    requestPayrollElection,
    updateUserName,
    updateUserLanguagePreference,
    updateUserBirthday,
    updateUserAddress,
    updateUserPhoneNumber,
    updateUserTaxId,
    activateUserEmployment,
    updateUserFundingSource,
    updateUserResidencyStatus,
    updateUserDisclosures,
    updateUserMaritalStatus,
    updateUserBeneficiaries,
    updateUserTrustedContact,
    getRequiredAgreements,
    acceptAgreements,
    updateInvestmentAccountType,
    submitInvestmentAccount,
    updateUserEmploymentStatus,
    updateUserAccountLossRiskQuestion,
    updateUserInvestmentObjectiveRiskQuestion,
    updateUserIncomeLevel,
    updateUserNetWorth,
    updateUserIsThirdPartyPayrollAdmin,
    updateUserAdminEmployer,
    completeUserAdminOnboarding,

    getUserRecurringDeposits,
    getUserTransferRequests,
    getUserTransferHistory,
    createRecurringDeposit,
    updateRecurringDeposit,
    cancelRecurringDeposit,
    createIncomingTransferRequest,
    createOutgoingTransferRequest,
    cancelTransferRequest,

    getPlaidLinkedAccounts,
    getPlaidLinkedAccountsAsList,
    requestPlaidLinkToken,
    connectPlaidPublicToken,
    getInvestmentAccounts,
    getInvestmentAccountStatements,
    downloadInvestmentAccountStatement,
    getUserTransferOptions,
    createEmployer,
    updateEmployer,
    getAdministratedEmployers,
    getEmployeesForEmployer,
    addEmployeeForEmployer,
    updateEmployeeForEmployer,
    resendCodeForEmployee,
    confirmNameForEmployee,
    confirmPayrollElectionEmployee,
  };
};

export { ServerValidationError, loginUser, getActivationCodeData, getClientParameters, createUserApi };

//TODO: Refactor to match current client api architecture
export const getBlogList = async () => {
  const response = await fetch('/api/blog/posts', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
};

export const getBlogPost = async (id) => {
  const response = await fetch(`/api/blog/post/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
};

export const leadRegistration = (userData) => {
  return fetch('/api/marketingLead/post', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });
};
